import { AddIcon } from "@chakra-ui/icons";
import { Flex, Text, TextProps } from "@chakra-ui/react";
import { DropzoneInputProps, DropzoneRootProps } from "react-dropzone";
import { FormattedMessage } from "react-intl";

import { RanaButton, RanaTable } from "@nens/ranalib";

import { useProjectActionAllowed } from "@/hooks/use-action-allowed-in-project";
import { useIconSrc } from "@/hooks/use-icon-src";

type TableEmptyFilesProps =
  | {
      open: () => void;
      getInputProps: <T extends DropzoneInputProps>(props?: T) => T;
      getRootProps: <T extends DropzoneRootProps>(props?: T) => T;
    }
  | {
      open?: () => void;
    };

const TextButton = (props: TextProps) => (
  <Text cursor="pointer" textDecor="underline" color="brand.analytic" {...props} />
);

export function TableEmptyFiles(props: TableEmptyFilesProps) {
  const { canWriteProject } = useProjectActionAllowed();
  const icon = useIconSrc("files");
  // Show the full "Upload files" button (with hidden input)
  // when `getInputProps` and `getRootProps` is provided (i.e. no external upload button exists).
  const showUploadButton = "getInputProps" in props;

  // Show a simple "Upload files" link instead of a button
  // when no input and root props are provided.
  const showUploadLink = !("getInputProps" in props);

  return (
    <RanaTable.Empty icon={icon}>
      {canWriteProject && showUploadLink && props.open ? (
        <TextButton onClick={props.open}>
          <FormattedMessage id="projectfiles.upload_files" defaultMessage="Upload files" />
        </TextButton>
      ) : (
        <Text>
          <FormattedMessage
            id="projectfiles.no_files_in_folder"
            defaultMessage="No files in this folder"
          />
        </Text>
      )}

      {canWriteProject && showUploadButton && (
        <Flex marginTop={4} {...props.getRootProps()}>
          <input {...props.getInputProps()} />
          <RanaButton variant="primary" onClick={props.open} leftIcon={<AddIcon boxSize={6} />}>
            <FormattedMessage id="button.add" defaultMessage="Add" />
          </RanaButton>
        </Flex>
      )}
    </RanaTable.Empty>
  );
}
